import { useEffect, useRef } from "react";

import { sendCoord, subscribeCoord } from "./channel";
import type { SessionState } from "./messages";

/**
 * Opener-side broadcaster for the one-window-per-document viewer (OR-22558).
 * Mounted in the Review page for a specific deal (`appId`); it tells every
 * open document window that deal's current session signal (auth + VLA lock),
 * since that's a property of the analyst's session/deal, not of any one
 * document.
 *
 * `heldByMe` is broadcast alongside `sessionState` but drives a different
 * concern: lock ownership for this deal. The move/check-in toolbar requires
 * both `heldByMe` and the shared matrix's `canManageDocuments`; neither
 * affects document content visibility. Every message is stamped with `appId` so a document window can
 * ignore broadcasts for a *different* deal — an analyst can have several
 * deals open as separate Review tabs at once, each with its own
 * `useDocumentBroadcaster` instance broadcasting independently.
 *
 * Emits `session-state` whenever session, lock, or document access changes, and in
 * reply to a viewer's `request-session-state` handshake (sent when a document
 * window mounts, so it learns the current state immediately instead of
 * waiting for the next change).
 *
 * Deliberately no heartbeat and no `pagehide`/session-ended-on-blur: the
 * viewer is load-and-persist, so a backgrounded, reloaded, or closed opener
 * must not blank documents the analyst is reading. A visible-again or reloaded
 * opener re-asserts state via the handshake.
 * @param {SessionState} sessionState - The opener's current session signal.
 * @param {boolean} heldByMe - Whether the opener currently holds this deal's VLA lock.
 * @param {string} appId - The deal (approval id) this opener instance represents.
 * @param {boolean} canManageDocuments - Effective matrix permission for check-in and reassignment.
 */
export function useDocumentBroadcaster(
  sessionState: SessionState,
  heldByMe: boolean,
  appId: string,
  canManageDocuments = false
): void {
  // Ref so the handshake subscription (mounted once) always reads the latest
  // state without re-subscribing each render.
  const sessionStateReference = useRef(sessionState);
  sessionStateReference.current = sessionState;
  const heldByMeReference = useRef(heldByMe);
  heldByMeReference.current = heldByMe;
  const canManageDocumentsReference = useRef(canManageDocuments);
  canManageDocumentsReference.current = canManageDocuments;
  const appIdReference = useRef(appId);
  appIdReference.current = appId;

  // Emit `session-state` whenever either signal changes. Any tab can send
  // updates; BroadcastChannel uses last-writer-wins semantics.
  useEffect(() => {
    if (!appId) {
      return;
    }
    sendCoord({ type: "session-state", appId, sessionState, heldByMe, canManageDocuments });
  }, [sessionState, heldByMe, appId, canManageDocuments]);

  // Reply to a document window's mount-time handshake with the current state.
  // Any tab can respond; every open document window receives it (and ignores
  // the reply if its own deal's appId doesn't match).
  useEffect(() => {
    const unsubscribe = subscribeCoord((message) => {
      if (message.type === "request-session-state" && appIdReference.current) {
        sendCoord({
          type: "session-state",
          appId: appIdReference.current,
          sessionState: sessionStateReference.current,
          heldByMe: heldByMeReference.current,
          canManageDocuments: canManageDocumentsReference.current,
        });
      }
    });
    return unsubscribe;
  }, []);
}
